
import { useState, useEffect, useCallback } from 'react';
import authenticatedImageAPI from '../services/authenticatedImageAPI';

// Failed images cache to prevent infinite retries
const failedImages = new Set();

/**
 * Hook for loading authenticated images with caching and error handling
 * @param {string} imagePath - Path to the image
 * @param {string} size - Image size (small, medium, large, original)
 * @param {Object} options - Additional options
 * @returns {Object} { imageSrc, isLoading, hasError, reload }
 */
export const useAuthenticatedImage = (imagePath, size = 'medium', options = {}) => {
  const {
    fallbackSrc = '/images/default-avatar.svg',
    enabled = true,
    onLoad = null,
    onError = null
  } = options;

  const [imageSrc, setImageSrc] = useState(fallbackSrc);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const loadImage = useCallback(async () => {
    if (!enabled || !imagePath) {
      setImageSrc(fallbackSrc);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    // Don't process static images that start with / or http - just use them directly
    if (imagePath.startsWith('/') || imagePath.startsWith('http')) {
      setImageSrc(imagePath);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    // Create cache key for failed images
    const cacheKey = `${imagePath}_${size}`;
    
    // If this image has previously failed, don't retry - just show fallback
    if (failedImages.has(cacheKey)) {
      setImageSrc(fallbackSrc);
      setIsLoading(false);
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const objectUrl = await authenticatedImageAPI.fetchImage(imagePath, size);
      
      if (objectUrl) {
        setImageSrc(objectUrl);
        setHasError(false);
        if (onLoad) onLoad(objectUrl);
      } else {
        throw new Error('No image URL returned');
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load authenticated image:', error);
      
      // Add to failed images cache to prevent retries
      failedImages.add(cacheKey);
      
      setHasError(true);
      setImageSrc(fallbackSrc);
      setIsLoading(false);
      
      if (onError) onError(error);
    }
  }, [imagePath, size, enabled, fallbackSrc, onLoad, onError]);

  const reload = useCallback(() => {
    // Remove from failed cache and retry
    const cacheKey = `${imagePath}_${size}`;
    failedImages.delete(cacheKey);
    loadImage();
  }, [loadImage, imagePath, size]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  return {
    imageSrc,
    isLoading,
    hasError,
    reload
  };
};

/**
 * Hook for preloading multiple authenticated images
 * @param {Array} imagePaths - Array of image paths to preload
 * @param {string} size - Image size to preload
 * @returns {Object} { isLoading, hasError, loadedCount, totalCount, reload }
 */
export const usePreloadAuthenticatedImages = (imagePaths = [], size = 'medium') => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);

  const preload = useCallback(async () => {
    if (!imagePaths.length) {
      setLoadedCount(0);
      setIsLoading(false);
      setHasError(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);
    setLoadedCount(0);

    try {
      const results = await authenticatedImageAPI.preloadImages(imagePaths, size);
      
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const hasErrors = results.some(result => result.status === 'rejected');
      
      setLoadedCount(successCount);
      setHasError(hasErrors);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to preload images:', error);
      setHasError(true);
      setIsLoading(false);
    }
  }, [imagePaths, size]);

  const reload = useCallback(() => {
    preload();
  }, [preload]);

  useEffect(() => {
    preload();
  }, [preload]);

  return {
    isLoading,
    hasError,
    loadedCount,
    totalCount: imagePaths.length,
    reload
  };
};

/**
 * Hook for managing authenticated image cache
 * @returns {Object} { clearCache, cacheSize }
 */
export const useAuthenticatedImageCache = () => {
  const [cacheSize, setCacheSize] = useState(0);

  const clearCache = useCallback(() => {
    authenticatedImageAPI.clearCache();
    setCacheSize(0);
  }, []);

  const updateCacheSize = useCallback(() => {
    // This is a rough estimate since we don't expose cache size from the API
    // You might want to add a getCacheSize method to the API service
    setCacheSize(authenticatedImageAPI.cache?.size || 0);
  }, []);

  useEffect(() => {
    updateCacheSize();
    
    // Update cache size periodically
    const interval = setInterval(updateCacheSize, 5000);
    
    return () => clearInterval(interval);
  }, [updateCacheSize]);

  return {
    clearCache,
    cacheSize
  };
};
